import React, { FunctionComponent } from 'react';
import { Trans, useTranslation } from 'react-i18next';

import { chevronDownIcon, chevronUpIcon } from '@justgiving/icons';
import SVG from '@justgiving/svg';
import cx from 'classnames';

import Button from '../Button/Button';
import IconButton from '../IconButton/IconButton';
import StravaLogo from './StravaLogo';

import styles from './StravaConnectBar.scss';

type StravaConnectBarProps = {
  onToggle: () => void;
  isOpen: boolean;
  header?: string | JSX.Element;
  onStravaAuth?: () => void;
  onModalOpen?: () => void;
  hasSettings?: boolean;
  isConnected?: boolean;
  isPaused?: boolean;
};

export const StravaConnectBar: FunctionComponent<StravaConnectBarProps> = ({
  isOpen = false,
  onToggle,
  onModalOpen,
  onStravaAuth,
  isConnected,
  isPaused,
  hasSettings,
}) => {
  const { t } = useTranslation('common', { useSuspense: false });

  const content = (() => {
    if (isPaused) {
      return {
        title: t('strava.userPausedTitle'),
        status: t('strava.userPausedStatus'),
        body: t('strava.userPausedBody'),
        className: styles.paused,
        buttonText: (
          <Trans
            i18nKey="strava.settingsButton"
            components={[<StravaLogo />]}
          />
        ),
        onClick: onModalOpen,
        trackingData: {
          event: 'link click',
          subtype: 'button',
          page_section: 'main fundraising page',
        },
      };
    }

    if (isConnected && !hasSettings) {
      return {
        title: t(
          'strava.userConnectedTitle',
          'You can have your Strava activities automatically posted to this page ',
        ),
        status: t('strava.statusConnect', 'connect'),
        body: t(
          'strava.userConnectedBody',
          'Manage the settings for this page so that your activities will display.',
        ),
        buttonText: (
          <Trans
            i18nKey="strava.settingsButton"
            components={[<StravaLogo />]}
          />
        ),
        onClick: onModalOpen,
        trackingData: {
          event: 'link click',
          subtype: 'button',
          page_section: 'main fundraising page',
        },
      };
    }

    if (isConnected && hasSettings) {
      return {
        title: t('strava.settingTitle', 'Strava is connected'),
        status: t('strava.statusConnected', 'connected'),
        body: t(
          'strava.settingBody',
          'Your Strava activities are being shared to your page automatically.',
        ),
        buttonText: (
          <Trans
            i18nKey="strava.settingsButton"
            components={[<StravaLogo />]}
          />
        ),
        onClick: onModalOpen,
        trackingData: {
          event: 'link click',
          subtype: 'button',
          page_section: 'main fundraising page',
        },
      };
    }

    return {
      title: t(
        'strava.connectTitle',
        'Get your Strava activities automatically posted to your page',
      ),
      status: t('strava.statusConnect', 'connect'),
      body: t(
        'strava.connectBody',
        `Don't worry, you'll always have full control over what's posted.`,
      ),
      buttonText: (
        <Trans i18nKey="strava.connectButton" components={[<StravaLogo />]} />
      ),
      onClick: onStravaAuth,
      trackingData: {
        event: 'link click',
        subtype: 'button',
        page_section: 'main fundraising page',
      },
    };
  })();

  return (
    <div className={cx(styles.wrapper, content.className)}>
      <div
        className={cx(styles.bar, 'jg-space-psm', 'jg-space-pmd@lg')}
        aria-expanded={isOpen}
      >
        <div>
          <StravaLogo aria-label="Strava icon" className={styles.logo} />
          &nbsp;
          <p className={styles.status}>{content.status}</p>
        </div>
        <IconButton aria-label={isOpen ? 'Close' : 'Expand'} onClick={onToggle}>
          <SVG
            markup={isOpen ? chevronUpIcon : chevronDownIcon}
            className={styles.icon}
          />
        </IconButton>
      </div>

      {isOpen && (
        <div className={styles.container}>
          <h5>{content.title}</h5>
          <p className="jg-space-mbml">{content.body}</p>

          <Button
            type="button"
            onClick={content.onClick}
            fullWidth
            className={styles.button}
            trackingData={content.trackingData}
          >
            {content.buttonText}
          </Button>
        </div>
      )}
    </div>
  );
};

export default StravaConnectBar;
